import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationAccessCheck } from '@skynet/authz-navigation';
import type { NavigationPermissionsAdapter } from '@skynet/authz-navigation';
import { selectJwtPrincipal } from '@skynet/data-auth-api';
import type { AuthTokenState } from '@skynet/data-auth-api';
import { getPrincipalKey } from '@skynet/authz-provider';
import { navigationPermissionsActions } from '@skynet/data-navigation';
import type { NavigationState } from '@skynet/data-navigation';
import {
  selectNavigation,
  selectNavigationIsLoading,
  selectNavigationHasFailed,
} from 'src/store/navigation/selectors';
import { uiNavigationActions } from 'src/routes/navigation-config';

/**
 * The store slices this hook reads, under their root reducer keys. A subset of `RootState`,
 * which is not imported: the `src/store` / `src/hooks` modules would put the whole store into
 * this hook's (and `useSection`'s) import graph.
 *
 * Temporary deviation from `useAppSelector` / `useAppDispatch` (SKY-4991): switch back once
 * `RootState` / `AppDispatch` move out of `src/store/index.ts`. Until then this file cannot
 * prove the store has these slices — `useCheckNavigationAccess.test.tsx` asserts it — and
 * dispatch does not see store-specific extensions of `AppDispatch`. Don't copy the pattern
 * without such a test.
 */
export interface NavigationAccessState {
  navigationPermissions: NavigationState;
  authToken: AuthTokenState;
}

// Typed with `withTypes`, exactly like `useAppDispatch` / `useAppSelector` (tests mock
// `useDispatch.withTypes` / `useSelector.withTypes`). `ThunkDispatch` rather than plain
// `Dispatch`, so thunks type-check here as they do with `AppDispatch`.
const useNavigationDispatch =
  useDispatch.withTypes<ThunkDispatch<NavigationAccessState, unknown, UnknownAction>>();
const useNavigationSelector = useSelector.withTypes<NavigationAccessState>();

export const useCheckNavigationAccess = () => {
  const dispatch = useNavigationDispatch();
  const permissions = useNavigationSelector(selectNavigation);
  const isLoading = useNavigationSelector(selectNavigationIsLoading);
  const isError = useNavigationSelector(selectNavigationHasFailed);
  const principal = useNavigationSelector(selectJwtPrincipal);
  const principalKey = principal ? getPrincipalKey(principal) : null;

  const adapter: NavigationPermissionsAdapter = {
    permissions,
    isLoading,
    isError,
    principalKey,
    setPermissions: (perms) =>
      dispatch(navigationPermissionsActions.setData({ data: perms, principalKey })),
    setLoading: () => dispatch(navigationPermissionsActions.setLoading('pending')),
    setError: () => dispatch(navigationPermissionsActions.setLoading('error')),
  };

  return useNavigationAccessCheck(adapter, uiNavigationActions);
};
